/**
 * VÉRAULT — ExportMenu (Feb 2026)
 *
 * One understated control per client-facing Experience tab. Replaces
 * the previous three-buttons-side-by-side pattern (Preview / Download
 * / Print) with a single editorial dropdown so Export never competes
 * with the main client CTA.
 *
 * Usage:
 *   <ExportMenu
 *     label="Export"
 *     downloadUrl={`${API}/proposals/${tripId}/proposal.pdf`}
 *     downloadFilename={`verault-proposal-${slug}.pdf`}
 *     printUrl={`/portal/client/experience/${tripId}/proposal?print=1`}
 *     canPreview
 *   />
 *
 * The component renders one understated chip; clicking it opens a small
 * dropdown with the requested options.
 */
import React, { useState, useRef, useEffect } from "react";
import { Share2, Download, Eye, Printer, ChevronDown } from "lucide-react";

export default function ExportMenu({
  label = "Export",
  downloadUrl,
  downloadFilename,
  previewUrl,
  printUrl,
  canPreview = true,
  canDownload = true,
  canPrint = true,
  variant = "ghost", // "ghost" | "primary"
  testid = "export-menu",
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const triggerClass =
    variant === "primary"
      ? "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(var(--accent-gold))]/20 hover:bg-[hsl(var(--accent-gold))]/30 text-[hsl(var(--foreground))]"
      : "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-transparent border border-[hsl(var(--surface-hairline))] hover:bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]";

  const handlePreview = () => {
    setOpen(false);
    const url = previewUrl || downloadUrl;
    if (url) window.open(url, "_blank", "noopener");
  };
  const handleDownload = () => {
    setOpen(false);
    if (!downloadUrl) return;
    const a = document.createElement("a");
    a.href = downloadUrl;
    if (downloadFilename) a.download = downloadFilename;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  const handlePrint = () => {
    setOpen(false);
    const url = printUrl || previewUrl || downloadUrl;
    if (!url) {
      window.print();
      return;
    }
    // Open in a new tab so the client never loses their place. The
    // target page can auto-trigger window.print() if the URL has ?print=1.
    window.open(url, "_blank", "noopener");
  };

  return (
    <div className="relative inline-block" ref={ref} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${triggerClass} font-mono text-[10.5px] uppercase tracking-[0.20em]`}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={`${testid}-trigger`}
      >
        <Share2 className="size-3.5" strokeWidth={1.5} />
        {label}
        <ChevronDown className={`size-3 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
      </button>
      {open && (
        <div
          className="absolute right-0 z-30 mt-1 w-52 origin-top-right rounded-md border border-[hsl(var(--surface-hairline))] bg-[hsl(var(--background))] shadow-lg overflow-hidden"
          role="menu"
          data-testid={`${testid}-menu`}
        >
          {canPreview && (
            <button
              type="button"
              role="menuitem"
              onClick={handlePreview}
              disabled={!(previewUrl || downloadUrl)}
              className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[hsl(var(--background-paper))] disabled:opacity-50 disabled:cursor-not-allowed text-left"
              data-testid={`${testid}-preview`}
            >
              <Eye className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
              <span className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground))]">
                Preview PDF
              </span>
            </button>
          )}
          {canDownload && (
            <button
              type="button"
              role="menuitem"
              onClick={handleDownload}
              disabled={!downloadUrl}
              className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[hsl(var(--background-paper))] disabled:opacity-50 disabled:cursor-not-allowed text-left"
              data-testid={`${testid}-download`}
            >
              <Download className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
              <span className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground))]">
                Download PDF
              </span>
            </button>
          )}
          {canPrint && (
            <button
              type="button"
              role="menuitem"
              onClick={handlePrint}
              className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[hsl(var(--background-paper))] text-left"
              data-testid={`${testid}-print`}
            >
              <Printer className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
              <span className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground))]">
                Print
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
